import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../utils/cn';
import { IconButton } from '../primitives/Button/Button';

/**
 * DashboardLayout Component
 *
 * Mobile-first responsive layout for search dashboards.
 * Uses standard Tailwind classes for maximum compatibility.
 */

// Icons
const FilterIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const CloseIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

export function DashboardLayout({
  children,
  sidebar,
  header,
  title,
  subtitle,
  activeFilterCount = 0,
  onBack,
  className,
  ...props
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when sidebar open on mobile
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen, isMobile]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  return (
    <div
      className={cn(
        'min-h-screen',
        'bg-gray-50',
        className
      )}
      {...props}
    >
      {/* Page Header */}
      {(header || title) && (
        <header className="sticky top-14 z-10 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between py-2.5 min-h-[52px]">
              <div className="flex items-center gap-4">
                {onBack && (
                  <IconButton
                    icon={ChevronLeftIcon}
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    label="Go back"
                    className="hover:bg-gray-100"
                  />
                )}
                <div>
                  {title && (
                    <h1 className="text-base font-semibold text-gray-900 tracking-tight leading-tight">
                      {title}
                    </h1>
                  )}
                  {subtitle && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {header && (
                <div className="flex items-center gap-2">
                  {header}
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sidebar - Desktop: visible, Mobile/Tablet: slide-over */}
          {sidebar && (
            <>
              {/* Desktop Sidebar */}
              <aside className="hidden lg:block lg:col-span-3">
                <div className="sticky top-24 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Sidebar header */}
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FilterIcon className="w-5 h-5 text-gray-500" />
                      <span className="font-semibold text-gray-900">
                        Filters
                      </span>
                      {activeFilterCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[1.35rem] h-[1.35rem] px-1.5 bg-blue-600 text-white text-xs font-bold rounded-full">
                          {activeFilterCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    {sidebar}
                  </div>
                </div>
              </aside>

              {/* Mobile/Tablet Sidebar Overlay */}
              <div
                className={cn(
                  'lg:hidden',
                  'fixed inset-0 z-50',
                  'transition-opacity duration-300',
                  sidebarOpen
                    ? 'opacity-100 pointer-events-auto'
                    : 'opacity-0 pointer-events-none'
                )}
              >
                {/* Backdrop */}
                <div
                  className={cn(
                    'absolute inset-0',
                    'bg-gray-900/60',
                    'backdrop-blur-sm',
                    'transition-opacity duration-300',
                    sidebarOpen ? 'opacity-100' : 'opacity-0'
                  )}
                  onClick={toggleSidebar}
                  aria-hidden="true"
                />

                {/* Sidebar Panel */}
                <div
                  className={cn(
                    'absolute inset-y-0 left-0',
                    'w-full max-w-sm',
                    'bg-white',
                    'shadow-2xl',
                    'transition-transform duration-300 ease-out',
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                  )}
                >
                  {/* Sidebar Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FilterIcon className="w-5 h-5 text-gray-500" />
                      <span className="font-semibold text-gray-900">
                        Filters
                      </span>
                      {activeFilterCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[1.35rem] h-[1.35rem] px-1.5 bg-blue-600 text-white text-xs font-bold rounded-full">
                          {activeFilterCount}
                        </span>
                      )}
                    </div>
                    <IconButton
                      icon={CloseIcon}
                      variant="ghost"
                      size="sm"
                      onClick={toggleSidebar}
                      label="Close filters"
                      className="hover:bg-gray-100"
                    />
                  </div>

                  {/* Sidebar Content */}
                  <div className="p-5 overflow-y-auto max-h-[calc(100vh-4rem)]">
                    {sidebar}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Main Content */}
          <main
            className={cn(
              sidebar ? 'lg:col-span-9' : 'lg:col-span-12'
            )}
          >
            {children}
          </main>
        </div>
      </div>

      {/* Mobile Filter FAB */}
      {sidebar && (
        <button
          onClick={toggleSidebar}
          className={cn(
            'lg:hidden',
            'fixed bottom-6 right-6',
            'z-40',
            'flex items-center gap-2',
            'px-5 py-3.5',
            'bg-blue-600 hover:bg-blue-700',
            'text-white',
            'rounded-full',
            'shadow-lg hover:shadow-xl',
            'active:scale-95',
            'transition-all duration-150',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-600'
          )}
        >
          <FilterIcon className="w-5 h-5" />
          <span className="font-semibold text-sm">Filters</span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.35rem] h-[1.35rem] px-1.5 bg-white text-blue-600 text-xs font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Results Header - Stats bar for search results
 */
export function ResultsHeader({
  total,
  showing,
  sortOptions,
  selectedSort,
  onSortChange,
  viewOptions,
  selectedView,
  onViewChange,
  className,
  ...props
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between',
        'mb-6 pb-5',
        'border-b border-gray-200',
        className
      )}
      {...props}
    >
      <div className="text-[15px] text-gray-600">
        {showing && total ? (
          <>
            Showing{' '}
            <span className="font-semibold text-gray-900">{showing}</span>
            {' '}of{' '}
            <span className="font-semibold text-gray-900">{total.toLocaleString()}</span>
            {' '}results
          </>
        ) : total ? (
          <>
            <span className="font-semibold text-gray-900">{total.toLocaleString()}</span>
            {' '}results found
          </>
        ) : (
          <span className="animate-pulse">Loading results...</span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Sort dropdown */}
        {sortOptions && (
          <select
            value={selectedSort}
            onChange={(e) => onSortChange && onSortChange(e.target.value)}
            className="px-3 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg text-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer transition-shadow duration-150"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {/* View toggle (grid/list) */}
        {viewOptions && (
          <div className="flex rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            {viewOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onViewChange && onViewChange(option.value)}
                className={cn(
                  'p-2',
                  'transition-all duration-150',
                  selectedView === option.value
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                )}
              >
                {option.icon}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Results Grid - Responsive grid for cards
 */
export function ResultsGrid({
  children,
  columns = 1,
  className,
  ...props
}) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
  };

  return (
    <div
      className={cn(
        'grid gap-5 lg:gap-6',
        gridCols[columns],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Pagination Component
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
  ...props
}) {
  if (totalPages <= 1) return null;

  const pages = [];
  const showEllipsisStart = currentPage > 3;
  const showEllipsisEnd = currentPage < totalPages - 2;

  // Always show first page
  pages.push(1);

  if (showEllipsisStart) {
    pages.push('...');
  }

  // Show pages around current
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (showEllipsisEnd) {
    pages.push('...');
  }

  // Always show last page
  if (totalPages > 1 && !pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return (
    <nav
      className={cn(
        'flex items-center justify-center gap-1.5',
        'mt-10 pt-8',
        'border-t border-gray-200',
        className
      )}
      {...props}
    >
      {/* Previous button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-all duration-150"
      >
        <ChevronLeftIcon className="w-5 h-5" />
      </button>

      {/* Page numbers */}
      {pages.map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          className={cn(
            'min-w-[2.75rem] h-11 px-3',
            'rounded-lg',
            'text-sm font-semibold',
            'transition-all duration-150',
            page === currentPage
              ? 'bg-blue-600 text-white shadow-md'
              : page === '...'
              ? 'text-gray-400 cursor-default'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          )}
        >
          {page}
        </button>
      ))}

      {/* Next button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-all duration-150"
      >
        <ChevronLeftIcon className="w-5 h-5 rotate-180" />
      </button>
    </nav>
  );
}

export default DashboardLayout;
